import type {IssueFieldSelection} from '@github-ui/issue-fields-shared/issue-field'
import {ConfirmationDialog} from '@primer/react'
import {Fragment} from 'react'

import {stripTimezone} from '../../utils/date-formatting'
import type {FieldsSectionFieldValues$data} from './__generated__/FieldsSectionFieldValues.graphql'
import {LABELS} from './constants/labels'
import {IssueFieldDateEditor} from './IssueFieldDateEditor'
import {IssueFieldNumberEditor} from './IssueFieldNumberEditor'
import {IssueFieldSingleSelectEditor} from './IssueFieldSingleSelectEditor'
import {IssueFieldTextEditor} from './IssueFieldTextEditor'

type FieldValue = NonNullable<NonNullable<FieldsSectionFieldValues$data['issueFieldValues']>['nodes']>[number]
type FieldValueField = NonNullable<
  NonNullable<NonNullable<NonNullable<FieldsSectionFieldValues$data['issueFieldValues']>['nodes']>[number]>['field']
>
type FieldValueValues = Omit<FieldValue, 'field' | 'value'>

// Date is included because DatePicker returns Date objects during issue creation,
// while the backend returns ISO date strings
export type SafeFieldValue = FieldValue & {
  field: FieldValueField & {id: string}
  value: string | Date | FieldValueValues
}

type EditorProps<T> = {
  fieldValue: SafeFieldValue
  readonly: boolean
  onCommit: IssueFieldOnCommitFn<T>
  className?: string
}

type PendingEditorProps<T> = Omit<EditorProps<T>, 'fieldValue'> & {
  fieldSelection: IssueFieldSelection
}

export type IssueFieldOnCommitFn<T> = (fieldId: string, value: T) => void

export type IssueFieldEditorsProps<T> = {
  errorPresent: Record<string, boolean>
  setErrorPresent: (fieldId: string, present: boolean) => void
} & EditorProps<T>

export function getIssueFieldEditor<T>({
  errorPresent,
  setErrorPresent,
  fieldValue,
  ...props
}: IssueFieldEditorsProps<T>) {
  if (!fieldValue.field) return null

  const fieldId = fieldValue.field.id
  // Only show error dialog for single select fields
  const showErrorDialog = fieldValue.field.dataType === 'SINGLE_SELECT'
  const editor = getEditor({fieldValue, ...props}, errorPresent[fieldId])

  return (
    <Fragment key={fieldValue.field.name}>
      {editor}
      {showErrorDialog && errorPresent[fieldId] && (
        <ErrorDialog onClose={() => setErrorPresent(fieldId, false)} message={LABELS.mutationErrorMessage} />
      )}
    </Fragment>
  )
}

export function getEditor<T>({fieldValue, readonly, onCommit, className}: EditorProps<T>, errorPresent?: boolean) {
  const isCleared = fieldValue.value === ''

  switch (fieldValue.field.dataType) {
    case 'TEXT':
      return (
        <IssueFieldTextEditor
          key={fieldValue.field.name}
          fieldId={fieldValue.field.id}
          fieldName={fieldValue.field.name || ''}
          initialValue={fieldValue.value || ''}
          readonly={readonly}
          onCommit={(fieldId, value) => onCommit(fieldId, value as T)}
          errorPresent={errorPresent}
          className={className}
        />
      )
    case 'SINGLE_SELECT':
      return (
        <IssueFieldSingleSelectEditor
          key={fieldValue.field.name}
          fieldId={fieldValue.field.id}
          fieldName={fieldValue.field.name || ''}
          initialValue={{
            name: isCleared || !fieldValue?.name ? '' : fieldValue.name,
            color: isCleared || !fieldValue?.color ? '' : fieldValue.color,
            description: isCleared || !fieldValue?.description ? '' : fieldValue.description,
          }}
          readonly={readonly}
          onCommit={(fieldId, value) => onCommit(fieldId, value as T)}
          className={className}
        />
      )
    case 'DATE': {
      // fieldValue.value can be a Date object (during issue creation) or a string (from backend)
      let dateValue: Date | null = null
      const rawValue = fieldValue.value as Date | string | null
      if (rawValue) {
        if (rawValue instanceof Date) {
          dateValue = rawValue
        } else if (typeof rawValue === 'string') {
          dateValue = stripTimezone(rawValue)
        }
      }
      return (
        <IssueFieldDateEditor
          key={fieldValue.field.name}
          fieldId={fieldValue.field.id}
          fieldName={fieldValue.field.name || ''}
          value={dateValue}
          readonly={readonly}
          onCommit={(fieldId, value) => onCommit(fieldId, value as T)}
          errorPresent={errorPresent}
          className={className}
        />
      )
    }
    case 'NUMBER': {
      // Display empty string for number fields without values so the text editor shows the styled 'None yet' placeholder instead of defaulting to '0'
      // Handle both numberValue (from GraphQL/database) and value (from component state during creation)
      const hasNumberValue = fieldValue.numberValue !== undefined && fieldValue.numberValue !== null
      const hasValue = fieldValue.value !== undefined && fieldValue.value !== null && fieldValue.value !== ''

      const numberValue = hasNumberValue ? fieldValue.numberValue.toString() : hasValue ? String(fieldValue.value) : ''

      return (
        <IssueFieldNumberEditor
          key={fieldValue.field.name}
          fieldId={fieldValue.field.id}
          fieldName={fieldValue.field.name || ''}
          // Pass empty string so text editor shows styled "None yet" when no number is set
          initialValue={numberValue}
          readonly={readonly}
          onCommit={(fieldId, value) => {
            // For when the field is cleared, pass empty string to allow proper clearing logic. Otherwise Number("") returns 0, which we don't want
            if (value === '') {
              onCommit(fieldId, value as T)
            } else {
              const numericValue = Number(value)
              onCommit(fieldId, numericValue as T)
            }
          }}
          errorPresent={errorPresent}
          className={className}
        />
      )
    }
    default:
      throw new Error(`Unsupported field type: ${fieldValue.field.dataType}`)
  }
}

export function getPendingEditor<T>({fieldSelection, onCommit, className}: PendingEditorProps<T>) {
  switch (fieldSelection.dataType) {
    case 'text':
      return (
        <IssueFieldTextEditor
          key={fieldSelection.id}
          fieldId={fieldSelection.id || ''}
          fieldName={fieldSelection.name || ''}
          initialValue=""
          readonly={false}
          onCommit={(fieldId, value) => onCommit(fieldId, value as T)}
          shouldOpen
          isDraft
          className={className}
        />
      )
    case 'single_select':
      return (
        <IssueFieldSingleSelectEditor
          key={fieldSelection.name}
          fieldId={fieldSelection.id}
          fieldName={fieldSelection.name || ''}
          initialValue={{
            name: '',
            color: '',
            description: '',
          }}
          readonly={false}
          onCommit={(fieldId, value) => onCommit(fieldId, value as T)}
          shouldOpen
          isDraft
          className={className}
        />
      )
    case 'date':
      return (
        <IssueFieldDateEditor
          key={fieldSelection.name}
          fieldId={fieldSelection.id}
          fieldName={fieldSelection.name || ''}
          value={null}
          readonly={false}
          onCommit={(fieldId, value) => onCommit(fieldId, value as T)}
          shouldOpen
          isDraft
          className={className}
        />
      )
    case 'number':
      return (
        <IssueFieldNumberEditor
          key={fieldSelection.id}
          fieldId={fieldSelection.id || ''}
          fieldName={fieldSelection.name || ''}
          initialValue=""
          readonly={false}
          onCommit={(fieldId, value) => onCommit(fieldId, value as T)}
          shouldOpen
          isDraft
          className={className}
        />
      )
    default:
      throw new Error(`Unsupported field type: ${fieldSelection.dataType}`)
  }
}

// Generic error dialog
export function ErrorDialog({onClose, message}: {onClose: () => void; message: string}) {
  return (
    <ConfirmationDialog title="Error" confirmButtonContent="OK" confirmButtonType="primary" onClose={onClose}>
      {message}
    </ConfirmationDialog>
  )
}

try{ ErrorDialog.displayName ||= 'ErrorDialog' } catch {}